import React, { useState, useEffect, useContext } from "react";
import {
	Box,
	Button,
	Typography,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Modal,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import FormConfig from "./FormConfig";
import { agregarDato, auditPost, obtenerDatosSites } from "../../api/config";
import { useTranslation } from "react-i18next";
import { GridContract } from "./GridContract";
import Swal from "sweetalert2";
import { GlobalContext } from "../../GlobalContext";
import { validateField } from "../../Labels/descriptions/LabelsDescriptionsRestrictions/Restrictions";

const Create = ({ open, onClose, onCreated }) => {
	const { isGranted, getModuleId, site_id } = useContext(GlobalContext);
	const { t } = useTranslation();

	// Labels dinámicos
	const contratoExtraLabel = t("lab_tool.contract.labels.contrato_extra");
	const mesesContratoLabel = t("lab_tool.contract.labels.meses_contrato");

	const [paso, setPaso] = useState(1);
	const [changedFields, setChangedFields] = useState({
		[contratoExtraLabel]: "false",
		[mesesContratoLabel]: "",
	});
	const [errors, setErrors] = useState({});
	const [formError, setFormError] = useState("");

	const [dataDay, setDataDay] = useState([]);
	const [dataPeriod, setDataPeriod] = useState([]);
	const [allDaysEmpty, setAllDaysEmpty] = useState(false);
	const [showError, setShowError] = useState(false);
	const [hasIncompletePeriods, setHasIncompletePeriods] = useState(false);

	const [tipoContratos, setTipoContratos] = useState([]);
	const [tipoContratoSeleccionado, setTipoContratoSeleccionado] = useState("");
	const [errorSelectTipoContrato, setErrorSelectTipoContrato] = useState(false);

	// Tooltips y labels para el FormConfig
	const tooltips = [
		t("lab_tool.contract.tooltips.nombre"),
		t("lab_tool.contract.tooltips.kronos_name"),
		t("lab_tool.contract.tooltips.productividad"),
		t("lab_tool.contract.tooltips.rotacion"),
		t("lab_tool.contract.tooltips.ausentismo"),
		t("lab_tool.contract.tooltips.cotsup"),
		t("lab_tool.contract.tooltips.cotinf"),
		t("lab_tool.contract.tooltips.dias_lab_maximos"),
		t("lab_tool.contract.tooltips.dias_lab_minimos"),
		t("lab_tool.contract.tooltips.contrato"),
		t("lab_tool.contract.tooltips.despido"),
		t("lab_tool.contract.tooltips.salario"),
		t("lab_tool.contract.tooltips.zcero"),
		t("lab_tool.contract.tooltips.hora_contrato"),
		t("lab_tool.contract.tooltips.contrato_extra"),
		t("lab_tool.contract.tooltips.meses_contrato"),
	];

	const labels = [
		t("lab_tool.contract.labels.nombre"),
		t("lab_tool.contract.labels.kronos_name"),
		t("lab_tool.contract.labels.productividad"),
		t("lab_tool.contract.labels.rotacion"),
		t("lab_tool.contract.labels.ausentismo"),
		t("lab_tool.contract.labels.cotsup"),
		t("lab_tool.contract.labels.cotinf"),
		t("lab_tool.contract.labels.dias_lab_maximos"),
		t("lab_tool.contract.labels.dias_lab_minimos"),
		t("lab_tool.contract.labels.contrato"),
		t("lab_tool.contract.labels.despido"),
		t("lab_tool.contract.labels.salario"),
		t("lab_tool.contract.labels.zcero"),
		t("lab_tool.contract.labels.hora_contrato"),
		t("lab_tool.contract.labels.contrato_extra"),
		t("lab_tool.contract.labels.meses_contrato"),
	];

	// Configuración de campos (sin cambios)
	const fieldConfigs = {
		[t("lab_tool.contract.labels.nombre")]: {
			minLength: 3,
			regex: /^(?=.*[A-Za-zÁÉÍÓÚáéíóú])[A-Za-zÁÉÍÓÚáéíóú0-9\s]+$/,
		},
		[t("lab_tool.contract.labels.kronos_name")]: {
			minLength: 3,
			regex: /^(?=.*[A-Za-zÁÉÍÓÚáéíóú])[A-Za-zÁÉÍÓÚáéíóú0-9\s]+$/,
		},
		[t("lab_tool.contract.labels.productividad")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.contract.labels.rotacion")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.contract.labels.zcero")]: {
			type: "number",
			allowDecimal: false,
		},
		[t("lab_tool.contract.labels.cotsup")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.contract.labels.cotinf")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.contract.labels.ausentismo")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.contract.labels.contrato")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.contract.labels.despido")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.contract.labels.salario")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.contract.labels.hora_contrato")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.contract.labels.dias_lab_maximos")]: {
			type: "number",
			allowDecimal: false,
		},
		[t("lab_tool.contract.labels.dias_lab_minimos")]: {
			type: "number",
			allowDecimal: false,
		},
		[t("lab_tool.contract.labels.contrato_extra")]: {
			type: "checkbox",
		},
		[t("lab_tool.contract.labels.meses_contrato")]: {
			allowDecimal: false,
			type: "text",
			dependsOn: t("lab_tool.contract.labels.contrato_extra"),
			dependsValue: "true",
		},
	};

	// Fetch tipos de contrato
	useEffect(() => {
		const fetchTipos = async () => {
			try {
				const resp = await obtenerDatosSites(`/tipo_contrato/get?site_id=${site_id}`);
				setTipoContratos(resp);
			} catch (err) {
				console.error(err);
			}
		};
		fetchTipos();
	}, [site_id]);
	// Campo a campo (sin validar checkbox en tiempo real)
	const handleFieldChange = (field, value) => {
		const upd = { ...changedFields, [field]: value };
		if (field === contratoExtraLabel && value === "false") upd[mesesContratoLabel] = null;
		setChangedFields(upd);
		if (field === contratoExtraLabel || field === mesesContratoLabel) {
			setErrors((e) => ({
				...e,
				[field]: "",
				...(field === contratoExtraLabel ? { [mesesContratoLabel]: "" } : {}),
			}));
		} else {
			setErrors((e) => ({ ...e, [field]: validateField(field, value, upd, t) }));
		}
	};

	const handleButtonClickPaso1 = () => {
		const required = [
			t("lab_tool.contract.labels.nombre"),
			t("lab_tool.contract.labels.kronos_name"),
			t("lab_tool.contract.labels.productividad"),
			t("lab_tool.contract.labels.rotacion"),
			t("lab_tool.contract.labels.ausentismo"),
			t("lab_tool.contract.labels.cotsup"),
			t("lab_tool.contract.labels.cotinf"),
			t("lab_tool.contract.labels.dias_lab_maximos"),
			t("lab_tool.contract.labels.dias_lab_minimos"),
			t("lab_tool.contract.labels.contrato"),
			t("lab_tool.contract.labels.despido"),
			t("lab_tool.contract.labels.salario"),
			t("lab_tool.contract.labels.zcero"),
			t("lab_tool.contract.labels.hora_contrato"),
		];
		if (changedFields[contratoExtraLabel] === "true") required.push(mesesContratoLabel);
		const newErr = {};
		required.forEach((lbl) => {
			newErr[lbl] = validateField(lbl, changedFields[lbl], changedFields, t);
		});
		setErrors(newErr);
		if (Object.values(newErr).some((e) => e)) {
			setFormError(t("aux.messages.fix_error"));
		} else {
			setFormError("");
			setPaso(2);
		}
	};

	const handleUpdateData = (daysArr, periodsArr, allEmptyStatus) => {
		setDataDay(daysArr);
		setDataPeriod(periodsArr);
		setAllDaysEmpty(allEmptyStatus);
		setShowError(allEmptyStatus);
		const incomplete = periodsArr.some((d) => d.periods.some((p) => !p.start_time || !p.end_time));
		setHasIncompletePeriods(incomplete);
	};

	const validatePeriods = (arr) =>
		arr.every((d) => d.periods.every((p) => p.start_time && p.end_time));
	const hasErrorsOrIncompleteFields = () => {
		const errs = Object.values(errors).some((e) => e);
		const always = [
			t("lab_tool.contract.labels.nombre"),
			t("lab_tool.contract.labels.kronos_name"),
			t("lab_tool.contract.labels.productividad"),
			t("lab_tool.contract.labels.rotacion"),
			t("lab_tool.contract.labels.ausentismo"),
			t("lab_tool.contract.labels.cotsup"),
			t("lab_tool.contract.labels.cotinf"),
			t("lab_tool.contract.labels.dias_lab_maximos"),
			t("lab_tool.contract.labels.dias_lab_minimos"),
			t("lab_tool.contract.labels.contrato"),
			t("lab_tool.contract.labels.despido"),
			t("lab_tool.contract.labels.salario"),
			t("lab_tool.contract.labels.zcero"),
			t("lab_tool.contract.labels.hora_contrato"),
		];

		if (changedFields[contratoExtraLabel] === "true") always.push(mesesContratoLabel);
		const allFilled = always.every(
			// eslint-disable-next-line eqeqeq
			(lbl) => changedFields[lbl] !== undefined && changedFields[lbl] != ""
		);
		return errs || !allFilled || !tipoContratoSeleccionado;
	};

	// Función que se ejecuta al hacer clic en "Crear"
	const handleButtonCreate = () => {
		setFormError("");
		if (allDaysEmpty) {
			setShowError(true);
			setFormError(t("aux.messages.alert.grid_d_p.error"));
			return;
		}
		if (hasErrorsOrIncompleteFields()) {
			setFormError(t("aux.messages.error_campos"));
			return;
		}
		if (!validatePeriods(dataPeriod)) {
			setFormError("Existen periodos incompletos.");
			return;
		}

		const datos = {
			id: 0,
			config_site_id: site_id,
			nombre: changedFields[t("lab_tool.contract.labels.nombre")],
			kronos_name: changedFields[t("lab_tool.contract.labels.kronos_name")],
			productividad: changedFields[t("lab_tool.contract.labels.productividad")],
			rotacion: changedFields[t("lab_tool.contract.labels.rotacion")],
			ausentismo: changedFields[t("lab_tool.contract.labels.ausentismo")],
			cotsup: changedFields[t("lab_tool.contract.labels.cotsup")],
			cotinf: changedFields[t("lab_tool.contract.labels.cotinf")],
			dias_lab_maximos: changedFields[t("lab_tool.contract.labels.dias_lab_maximos")],
			dias_lab_minimos: changedFields[t("lab_tool.contract.labels.dias_lab_minimos")],
			contrato: changedFields[t("lab_tool.contract.labels.contrato")],
			despido: changedFields[t("lab_tool.contract.labels.despido")],
			salario: changedFields[t("lab_tool.contract.labels.salario")],
			zcero: changedFields[t("lab_tool.contract.labels.zcero")],
			hora_contrato: changedFields[t("lab_tool.contract.labels.hora_contrato")],
			contrato_extra: changedFields[contratoExtraLabel],
			meses_contrato: changedFields[mesesContratoLabel],
			id_tipo_contrato: tipoContratoSeleccionado,
			contracts_per_day: dataDay,
			contracts_per_periods: dataPeriod,
		};

		onClose();
		Swal.fire({
			title: t("aux.messages.sure"),
			text: t("aux.messages.not_revert"),
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: t("aux.buttons.yes"),
		}).then(async (res) => {
			if (!res.isConfirmed) return;
			try {
				await agregarDato("/contracts/create", datos);
				auditPost(getModuleId("Configuración", "Crear Contrato", "Botón crear contrato"), site_id);
				onCreated();
				Swal.fire({
					icon: "success",
					title: t("aux.messages.alert.success"),
					text: t("aux.messages.alert.text_alert.create_contract"),
				});
			} catch (error) {
				Swal.fire({
					icon: "error",
					title: t("aux.messages.alert.error"),
					text: t("aux.messages.alert.text_alert.error_create_contract"),
				});
			}
		});
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
			sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
		>
			<Box
				sx={{
					width: { xs: "95%", sm: "80%", md: "60%" },
					maxHeight: "90vh",
					bgcolor: "background.paper",
					p: 2,
					overflowY: "auto",
				}}
			>
				{paso === 1 && (
					<Box>
						<Typography variant="h6">
							{t("aux.step")} 1 {t("aux.of")} 2 - {t("config.config_general")}
						</Typography>
						<FormControl fullWidth required error={errorSelectTipoContrato} sx={{ mb: 2 }}>
							<InputLabel>Tipo de contrato</InputLabel>
							<Select
								value={tipoContratoSeleccionado}
								onChange={(e) => {
									setTipoContratoSeleccionado(e.target.value);
									setErrorSelectTipoContrato(false);
								}}
							>
								{tipoContratos.map((tc) => (
									<MenuItem key={tc.id} value={tc.id}>
										{tc.nombre}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormConfig
							labels={labels}
							valorDefault={changedFields}
							handleFieldChange={handleFieldChange}
							tooltips={tooltips}
							errors={errors}
							fieldConfigs={fieldConfigs}
						/>
						{formError && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{formError}
							</Alert>
						)}
						<Button
							fullWidth
							variant="contained"
							onClick={handleButtonClickPaso1}
							disabled={!isGranted("Configuración", "Crear Contrato", "Botón crear contrato")}
						>
							{t("aux.buttons.continue")}
						</Button>
					</Box>
				)}
				{paso === 2 && (
					<Box>
						<Typography variant="h6">
							{t("aux.step")} 2 {t("aux.of")} 2 - {t("config.config_day_period")}
						</Typography>
						<GridContract onDataUpdate={handleUpdateData} showError={showError} />
						{formError && (
							<Alert severity="error" sx={{ mt: 2 }}>
								{formError}
							</Alert>
						)}
						<Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
							<Button variant="outlined" onClick={() => setPaso(1)}>
								{t("aux.buttons.back")}
							</Button>
							<Button
								variant="contained"
								onClick={handleButtonCreate}
								disabled={
									!isGranted("Configuración", "Crear Contrato", "Botón crear contrato") ||
									hasIncompletePeriods
								}
							>
								{t("aux.buttons.config.create")}
							</Button>
						</Box>
					</Box>
				)}
			</Box>
		</Modal>
	);
};

export { Create };
